import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  BarChart,
  BarStackChart,
  BarTornadoChart,
  ColumnChart,
  formatData,
  LineChart,
  NARRATIVE_CHART_COLORS,
  ScatterChart,
  TableData,
  tooltipFormatter,
  tooltipFormatterxy,
  tooltipMultiPointFormatter,
  twoDataLabelFormatter,
} from '@utilities';

import { getColorByVariant, getPptStructuredData, getTableColumns } from '../../utilities/helpers';

import Tree from '../../../../pages/Report/components/Tab/components/Visualizations/components/Tree';
import { transformTreeNarrativeData } from '../../../../pages/Report/components/Tab/components/Visualizations/utilities/tree_helpers';

import { getPreviewDataset } from '../../utilities/helpers';

import styles from './_index.module.scss';

const hasRenderableDataset = (value) => {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value.series) && value.series.length > 0) return true;
  if (Array.isArray(value.data) && value.data.length > 0) return true;
  if (Array.isArray(value.nodes) && value.nodes.length > 0) return true;
  if (Array.isArray(value.columns) && value.columns.length > 0) return true;
  return false;
};

const Visualization = ({
  dataset,
  exportData,
  isDraftCard = false,
  isExcluded,
  setDownloadData,
  topicId,
  topicTakeaways,
  useWindowScroll,
}) => {
  const shouldUsePreviewDataset = isDraftCard && !hasRenderableDataset(dataset);
  const previewDataset = shouldUsePreviewDataset ? getPreviewDataset(dataset?.type) : null;
  const resolvedDataset = previewDataset || dataset || {};
  const isPreviewDataset = Boolean(resolvedDataset.__isPreview);
  const {
    columns: dataColumns = [],
    data: tableData = [],
    nodes = [],
    series = [],
    type: resolvedDatasetType,
    xAxis = {},
    yAxis = {},
  } = resolvedDataset;
  const datasetType = resolvedDatasetType || dataset?.type || '';
  const [chartInstance, setChartInstance] = useState(null);
  const hideChartValues = isPreviewDataset || isDraftCard;

  const onChartCallback = (chart) => {
    setChartInstance(chart);
  };

  const updateDownloadData = (content) => {
    if (isDraftCard) return;
    if (content?.type === 'chart-image' && !chartInstance) {
      return;
    }

    setDownloadData((prevData) => {
      const data = new Map(prevData);
      const existingTopic = prevData?.get(topicId);
      if (isExcluded) {
        data.set(topicId, {
          ...existingTopic,
          content: [],
        });
      } else {
        const newContentChart = ['brand-tree-v2', 'chart-image'].includes(content?.type)
          ? chartInstance
          : content;
        const newContent = {
          data: newContentChart,
          readAs: dataset?.cleanReadAs || dataset?.readAs,
        };

        const isDuplicate = existingTopic?.content?.some((existing) => {
          if (['brand-tree-v2', 'chart-image'].includes(content?.type))
            return existing?.data === newContentChart;
          return JSON.stringify(existing) === JSON.stringify(newContent);
        });
        if (isDuplicate) return prevData;

        data.set(topicId, {
          ...existingTopic,
          content: [...(existingTopic?.content || []), newContent],
          id: topicId,
          type: content?.type,
        });
      }

      return data;
    });
  };

  useEffect(() => {
    if (!topicId || !dataset || isDraftCard) return;
    const structuredData = getPptStructuredData(dataset);
    if (!['brand-tree-v2', 'chart-image'].includes(structuredData?.type)) {
      updateDownloadData(structuredData);
    }
  }, [dataset, isDraftCard, isExcluded, topicId]);

  useEffect(() => {
    // Need useEffect for chart-image as the chart ref needs to be set before
    if (!chartInstance || !topicId || isDraftCard) return;
    const structuredData = getPptStructuredData(dataset);
    updateDownloadData(structuredData);
  }, [chartInstance, isDraftCard, isExcluded, topicId]);

  switch (datasetType) {
    case 'bar-chart': {
      const format = yAxis?.labels?.format || '0.0';
      return (
        <BarChart
          className={styles['visualization']}
          colors={NARRATIVE_CHART_COLORS}
          onChartCallback={onChartCallback}
          plotOptions={{
            bar: {
              dataLabels: hideChartValues
                ? { enabled: false }
                : {
                    formatter: function () {
                      return formatData({
                        format,
                        value: this.y,
                      });
                    },
                  },
            },
            series: {
              border: 'none',
              borderRadius: 3,
              groupPadding: 0.2,
            },
          }}
          prescriptText={topicTakeaways}
          series={series}
          showExportMenu={!hideChartValues}
          tooltip={
            hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return tooltipFormatter({ data: this, format });
                  },
                }
          }
          xAxis={xAxis}
          yAxis={{
            labels: hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return formatData({ format, value: this.value });
                  },
                },
          }}
        />
      );
    }
    case 'bar-chart-two-labels': {
      const format = yAxis?.labels?.format || '0.0';
      const formattedSeriesWithDataLabels = series.map((seriesItem) => {
        const originalDataLabels = Array.isArray(seriesItem.dataLabels)
          ? seriesItem.dataLabels
          : [];
        const disabledDataLabels =
          originalDataLabels.length > 0
            ? originalDataLabels.map(() => ({ enabled: false }))
            : [{ enabled: false }];

        return {
          ...seriesItem,
          dataLabels: hideChartValues
            ? disabledDataLabels
            : [
                {
                  format: null,
                  formatter: function () {
                    return formatData({
                      format: seriesItem.dataLabels?.[0]?.format,
                      value: this.y,
                    });
                  },
                },
                {
                  align: 'right',
                  alignTo: 'plotEdges',
                  crop: false,
                  format: null,
                  formatter: function () {
                    return twoDataLabelFormatter({
                      format: seriesItem.dataLabels?.[1]?.format,
                      value: this.y2,
                      yDelta: this.yDelta,
                    });
                  },
                  overflow: 'allow',
                  padding: -45,
                  useHTML: true,
                },
              ],
        };
      });
      return (
        <BarChart
          chart={{
            marginRight: 45,
          }}
          className={styles['visualization']}
          colors={NARRATIVE_CHART_COLORS}
          onChartCallback={onChartCallback}
          plotOptions={{
            series: {
              border: 'none',
              borderRadius: 3,
              groupPadding: 0.2,
            },
          }}
          prescriptText={topicTakeaways}
          series={formattedSeriesWithDataLabels}
          showExportMenu={!hideChartValues}
          tooltip={
            hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return tooltipFormatter({ data: this, format });
                  },
                }
          }
          xAxis={xAxis}
          yAxis={{
            labels: hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return formatData({ format, value: this.value });
                  },
                },
            showLastLabel: false,
          }}
        />
      );
    }
    case 'bar-stack-chart': {
      const format = yAxis?.labels?.format || '0.0';

      return (
        <BarStackChart
          chart={{
            marginLeft: 150,
            spacingRight: 0,
          }}
          className={styles['visualization']}
          colors={NARRATIVE_CHART_COLORS}
          onChartCallback={onChartCallback}
          plotOptions={{
            series: {
              dataLabels: hideChartValues
                ? { enabled: false }
                : {
                    enabled: true,
                    formatter: function () {
                      return formatData({
                        format,
                        value: this.y,
                      });
                    },
                    inside: true,
                  },
            },
          }}
          prescriptText={topicTakeaways}
          series={series}
          showExportMenu={!hideChartValues}
          tooltip={
            hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return tooltipFormatter({ data: this, format });
                  },
                }
          }
          xAxis={{ ...xAxis, lineWidth: 0 }}
          yAxis={{
            gridLineWidth: 0,
            labels: {
              enabled: false,
            },
          }}
        />
      );
    }
    case 'bar-tornado-chart':
      return (
        <BarTornadoChart
          className={styles['visualization']}
          colors={NARRATIVE_CHART_COLORS}
          onChartCallback={onChartCallback}
          prescriptText={topicTakeaways}
          series={series}
          showExportMenu={!hideChartValues}
          tooltip={hideChartValues ? { enabled: false } : resolvedDataset?.tooltip}
          xAxis={xAxis}
          yAxis={{
            ...yAxis,
            labels: hideChartValues ? { enabled: false } : yAxis?.labels,
          }}
        />
      );
    case 'brand-tree-v2':
    case 'ebsova-tree':
    case 'retailer-leakage-tree': {
      const treeData = transformTreeNarrativeData(nodes, datasetType);
      return (
        <div className={styles['visualization']}>
          <Tree
            data={treeData}
            exportData={isDraftCard ? null : exportData}
            hasPlaceholders={hideChartValues}
            nodeClassName={datasetType === 'ebsova-tree' ? styles['narrative-ebsova-tree'] : ''}
            onChartCallback={onChartCallback}
            prescriptText={topicTakeaways}
            showExportMenu={!hideChartValues}
            type="tree"
          />
        </div>
      );
    }
    case 'column-chart': {
      const format = yAxis?.labels?.format || '0.0';
      const topSeries = series.map((seriesItem) => ({
        ...seriesItem,
        data: seriesItem.data,
      }));
      const topXAxis = {
        ...xAxis,
        categories: xAxis?.categories || [],
      };

      return (
        <ColumnChart
          className={styles['visualization']}
          colors={NARRATIVE_CHART_COLORS}
          onChartCallback={onChartCallback}
          prescriptText={topicTakeaways}
          series={topSeries}
          showExportMenu={!hideChartValues}
          tooltip={
            hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return tooltipFormatter({ data: this, format });
                  },
                }
          }
          xAxis={topXAxis}
          yAxis={{
            labels: hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return formatData({ format, value: this.value });
                  },
                },
          }}
        />
      );
    }
    case 'line-chart': {
      const format = yAxis?.labels?.format || '0.0';
      return (
        <LineChart
          className={styles['visualization']}
          colors={NARRATIVE_CHART_COLORS}
          onChartCallback={onChartCallback}
          prescriptText={topicTakeaways}
          series={series}
          showExportMenu={!hideChartValues}
          tooltip={
            hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return tooltipMultiPointFormatter({ data: this, format });
                  },
                }
          }
          xAxis={xAxis}
          yAxis={{
            ...yAxis,
            labels: hideChartValues
              ? { enabled: false }
              : {
                  ...yAxis?.labels,
                  formatter: function () {
                    return formatData({ format, value: this.value });
                  },
                },
          }}
        />
      );
    }
    case 'scatter-chart': {
      const formattedSeries = series?.map((row) => ({
        ...row,
        data: Array.isArray(row?.data)
          ? row.data.map((value) => {
              const pointColor = value.color || getColorByVariant(value.colorType);

              return {
                ...value,
                color: pointColor,
                dataLabels: {
                  format: '{point.name}',
                  ...row.dataLabels,
                  ...value.dataLabels,
                  backgroundColor: pointColor,
                  borderColor: pointColor,
                },
              };
            })
          : [],
      }));
      const formatX = xAxis?.labels?.format || '0%';
      const formatY = yAxis?.labels?.format || '$0,0.00';

      return (
        <ScatterChart
          className={styles['visualization']}
          legend={false}
          onChartCallback={onChartCallback}
          prescriptText={topicTakeaways}
          series={formattedSeries}
          showExportMenu={!hideChartValues}
          tooltip={
            hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return tooltipFormatterxy({
                      data: this,
                      xAxis,
                      yAxis,
                    });
                  },
                }
          }
          xAxis={{
            labels: hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return formatData({ format: formatX, value: this.value });
                  },
                },
            max: xAxis?.max,
            plotLines: [
              // Only considering plotlines for focus product point
              {
                color: getColorByVariant('primary'),
                dashStyle: 'Dot',
                value: xAxis?.plotLines?.[0]?.value,
                width: 2,
              },
            ],
            title: {
              align: 'middle',
              margin: 20,
              text: xAxis?.title?.text,
            },
          }}
          yAxis={{
            labels: hideChartValues
              ? { enabled: false }
              : {
                  formatter: function () {
                    return formatData({ format: formatY, value: this.value });
                  },
                },
            max: yAxis?.max,
            plotLines: [
              {
                color: getColorByVariant('primary'),
                dashStyle: 'Dot',
                value: yAxis?.plotLines?.[0]?.value,
                width: 2,
              },
            ],
            title: {
              align: 'middle',
              margin: 20,
              rotation: 270,
              text: yAxis?.title?.text,
            },
          }}
        />
      );
    }
    case 'table': {
      const tableColumns = getTableColumns(dataColumns);
      return (
        <div className={styles['visualization']}>
          <TableData
            columns={tableColumns}
            copyEnabled={!hideChartValues}
            data={tableData}
            hasPlaceholders={hideChartValues}
            prescriptText={topicTakeaways}
            rowHeight={33}
            shouldTruncateHeaders={false}
            useWindowScroll={useWindowScroll}
          />
        </div>
      );
    }
    default:
      return <span>{`${datasetType} Visualization Unavailable`}</span>;
  }
};

Visualization.propTypes = {
  dataset: PropTypes.object.isRequired,
  exportData: PropTypes.object,
  isDraftCard: PropTypes.bool,
  isExcluded: PropTypes.bool,
  setDownloadData: PropTypes.func,
  topicId: PropTypes.number,
  topicTakeaways: PropTypes.node,
  useWindowScroll: PropTypes.bool,
};
export default Visualization;
